import { useQuery, useMutation } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import {
  adminFetchInvoiceImage,
  fetchAdminInvoices,
} from "../api/invoices.api";

interface UseAdminInvoicesProps {
  companyId?: number;
  skip?: number;
  take?: number;
  year?: number;
  month?: number;
  quarter?: number;
  invoiceNumber?: string;
}

const useAdminInvoices = ({
  companyId,
  skip,
  take,
  year,
  month,
  quarter,
  invoiceNumber,
}: UseAdminInvoicesProps) => {
  const [bearerToken] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken]),
  );

  const { data, error, isLoading } = useQuery({
    queryKey: [
      "admin-invoices",
      companyId,
      skip,
      take,
      year,
      month,
      quarter,
      invoiceNumber,
    ],
    queryFn: () =>
      fetchAdminInvoices(bearerToken as string, {
        companyId,
        skip,
        take,
        year,
        month,
        quarter,
        invoiceNumber,
      }),
    enabled: true,
  });

  const fetchInvoiceImageMutation = useMutation({
    mutationFn: (invoiceId: number) =>
      adminFetchInvoiceImage(bearerToken as string, invoiceId),
  });

  const downloadInvoiceImage = async (invoiceId: number) => {
    const imageBlob = await fetchInvoiceImageMutation.mutateAsync(invoiceId);

    // Create a temporary URL for the blob
    const url = window.URL.createObjectURL(imageBlob);

    // Create a temporary anchor element and trigger download
    const link = document.createElement("a");
    link.href = url;
    link.download = `sumion-${invoiceId}.png`;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return {
    invoiceData: data,
    invoiceError: error,
    isLoadingInvoices: isLoading,
    downloadInvoiceImage,
  };
};

export default useAdminInvoices;
